<template>
  <div v-if="loading" class="flex min-h-[400px] items-center justify-center txt-body-l-regular" data-testid="page-booth-order-summary">
    <div class="txt-body-l-medium">{{ t('BoothExtras.loading') }}</div>
  </div>
  <div
    v-else-if="booth && booth.extras && booth.extras.length"
    class="mx-auto max-w-[210mm] bg-surface p-5 font-sans print:m-0 print:max-w-full print:p-0"
    data-testid="page-booth-order-summary"
  >
    <!-- Header Section -->
    <div class="mb-8 border-b-2 pb-5 print:break-after-avoid">
      <div class="mb-2.5 flex items-start justify-between gap-6">
        <div class="max-w-[55%]">
          <h1 class="mb-3 txt-display-s-bold">
            {{ t('BoothExtras.orderSummary') }}
          </h1>
          <p class="txt-body-s-regular">
            {{ t('BoothExtras.orderSummaryDisclaimer') }}
          </p>
        </div>
        <div class="flex flex-col gap-2">
          <div class="flex gap-2 txt-body-s-regular">
            <span class="min-w-[120px] txt-body-s-semibold">{{ t('BoothExtras.boothNumber') }}:</span>
            <span class="">{{ booth.boothNumber || '-' }}</span>
          </div>
          <div class="flex gap-2 txt-body-s-regular">
            <span class="min-w-[120px] txt-body-s-semibold">{{ t('BoothExtras.boothName') }}:</span>
            <span class="">{{ booth.titleEn || '-' }}</span>
          </div>
          <div v-if="booth.partnerCompany" class="flex gap-2 txt-body-s-regular">
            <span class="min-w-[120px] txt-body-s-semibold">{{ t('BoothExtras.boothCompany') }}:</span>
            <span class="">{{ booth.partnerCompany.name }}</span>
          </div>
          <div class="flex gap-2 txt-body-s-regular">
            <span class="min-w-[120px] txt-body-s-semibold">{{ t('BoothExtras.eventName') }}:</span>
            <span>{{ booth.event.name }}</span>
          </div>
        </div>
      </div>
      <div class="mt-2.5 text-right txt-label-s-regular">{{ t('BoothExtras.printDate') }}: {{ formatDate(new Date().toISOString()) }}</div>
    </div>

    <!-- Order Items Table -->
    <div class="mb-10 print:break-inside-avoid">
      <h2 class="mb-4 txt-body-xl-semibold">{{ t('BoothExtras.orderedItems') }}</h2>
      <table class="w-full border-collapse txt-body-s-regular print:break-inside-auto">
        <thead class="border-b-2 print:table-header-group">
          <tr>
            <th class="px-2 py-3 text-left txt-label-s-semibold uppercase" width="45%">
              {{ t('BoothExtras.item') }}
            </th>
            <th class="px-2 py-3 text-right txt-label-s-semibold uppercase" width="20%">
              {{ t('BoothExtras.pricePerItem') }}
            </th>
            <th class="px-2 py-3 text-right txt-label-s-semibold uppercase" width="15%">
              {{ t('BoothExtras.amount') }}
            </th>
            <th class="px-2 py-3 text-right txt-label-s-semibold uppercase" width="20%">
              {{ t('BoothExtras.total') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="extra in extras" :key="extra.id" class="border-b last:border-b-0 print:break-inside-avoid">
            <td class="px-2 py-3 text-left align-top">
              <div class="mb-1 txt-body-s-semibold">Lorem</div>
              <div v-if="extra.variant" class="mb-0.5 txt-label-s-regular italic">
                {{ extra.variant.name }}
              </div>
              <div v-if="extra.comment" class="mt-1 border-t border-dotted pt-1 txt-label-s-regular">
                {{ extra.comment }}
              </div>
            </td>
            <td class="px-2 py-3 text-right">{{ formatPrice(extra.price) }}</td>
            <td class="px-2 py-3 text-right">{{ extra.count }}</td>
            <td class="px-2 py-3 text-right txt-body-s-semibold">{{ formatPrice(extra.price * extra.count) }}</td>
          </tr>

          <!-- Empty state -->
          <tr v-if="!extras || extras.length === 0">
            <td colspan="4" class="px-5 py-10 text-center italic">
              {{ t('BoothExtras.noItems') }}
            </td>
          </tr>
        </tbody>
        <tfoot v-if="extras && extras.length > 0" class="border-t-2 print:table-footer-group">
          <tr>
            <td colspan="3" class="px-2 py-2 text-right txt-body-s-semibold">
              {{ t('BoothExtras.subtotal') }}
            </td>
            <td class="px-2 py-2 text-right txt-body-s-semibold">{{ formatPrice(subtotal) }}</td>
          </tr>
          <tr v-if="totalDeliveryFee > 0">
            <td colspan="3" class="px-2 py-2 text-right txt-body-s-semibold">
              {{ t('BoothExtras.deliveryFee') }}
            </td>
            <td class="px-2 py-2 text-right txt-body-s-semibold">{{ formatPrice(totalDeliveryFee) }}</td>
          </tr>
          <tr class="txt-body-m-bold">
            <td colspan="3" class="px-2 py-3 text-right">
              {{ t('BoothExtras.total') }}
            </td>
            <td class="px-2 py-3 text-right">{{ formatPrice(total) }}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  </div>
  <div v-else class="container mt-6 max-w-xl" data-testid="page-booth-order-summary">
    <MessageBanner type="error" headline="No summary available" message="Booth summary you have requested doesn't exist." />
  </div>
</template>

<script setup lang="ts">
import { MessageBanner } from '@ramp106/omrjs-core-ui'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'

import { useBoothQuery, type Booth } from '@/gql/myomr'
import { toGlobalId } from '@/helpers/globalIdHelper'
import { formatPrice } from '@/helpers/moneyHelper'

const { t } = useI18n()
const route = useRoute()

const boothId = computed(() => toGlobalId('Booth', route.params.boothId as string))
const { result, loading } = useBoothQuery({ boothId: boothId.value })

const booth = computed(() => result.value?.node as Booth | undefined)
const extras = computed(() => booth.value?.extras || [])

const subtotal = computed(() => {
  return extras.value.reduce((sum, extra) => sum + extra.price * extra.count, 0)
})

const totalDeliveryFee = computed(() => {
  return extras.value.reduce((sum, extra) => sum + (extra.deliveryFee || 0), 0)
})

const total = computed(() => subtotal.value + totalDeliveryFee.value)

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('de-DE', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })
}

const hasTriggeredPrint = ref(false)

watch(
  [booth, loading],
  ([currentBooth, isLoading]) => {
    if (!isLoading && currentBooth && !hasTriggeredPrint.value && booth.value?.extras && booth.value.extras.length) {
      hasTriggeredPrint.value = true
      setTimeout(() => {
        window.print()
      }, 500)
    }
  },
  { flush: 'post' },
)
</script>

<style>
@media print {
  @page {
    size: A4;
    margin: 15mm;
  }

  body {
    print-color-adjust: exact;
    -webkit-print-color-adjust: exact;
  }
}
</style>
